import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SponsorBanner from "@/components/SponsorBanner";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, CalendarDays, Activity, Trophy, Sparkles } from "lucide-react";
import { renderScore, sportColor, SPORT_IMAGES } from "@/lib/sports";
import useFixtureSocket from "@/lib/useFixtureSocket";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

// Rotating hero backdrop — cycles through cricket, badminton, tennis, snooker,
// pickleball, basketball etc. Uses an incrementing counter stored in
// localStorage so every fresh visit lands on a different sport image.
const HERO_ROTATION = [
  "cricket", "badminton", "tennis", "basketball", "pickleball",
  "football", "tabletennis", "snooker", "volleyball", "chess",
];

function pickHeroImage() {
  try {
    const key = "kn_home_hero_idx";
    const prev = parseInt(localStorage.getItem(key) || "-1", 10);
    const next = (prev + 1) % HERO_ROTATION.length;
    localStorage.setItem(key, String(next));
    return SPORT_IMAGES[HERO_ROTATION[next]] || SPORT_IMAGES.football;
  } catch {
    // Private-mode / storage-blocked fallback: rotate by wall-clock minute.
    const idx = Math.floor(Date.now() / 60000) % HERO_ROTATION.length;
    return SPORT_IMAGES[HERO_ROTATION[idx]] || SPORT_IMAGES.football;
  }
}

export default function Home() {
  const [stats, setStats] = useState({ events: 0, teams: 0, players: 0, live: 0 });
  const [events, setEvents] = useState([]);
  const [liveFixtures, setLiveFixtures] = useState([]);
  const [teamMap, setTeamMap] = useState({});
  // Frozen for the lifetime of this mount — advances on the NEXT navigation to /.
  const [heroImage] = useState(pickHeroImage);

  const loadLive = async () => {
    try {
      const evRes = await api.get("/events");
      setEvents(evRes.data.slice(0, 3));
      const all = await Promise.all(
        evRes.data.map((e) => api.get(`/events/${e.id}/fixtures`).then((res) => res.data.map((f) => ({ ...f, event: e }))).catch(() => []))
      );
      // Defensive filter — a fixture can only show as LIVE if:
      //   • its own status is "live"
      //   • it has no winner_id / completed_at (i.e. not silently finalised)
      //   • parent event is NOT completed/cancelled (self-heal against stuck rows)
      const trulyLive = all.flat().filter((f) =>
        f.status === "live"
        && !f.winner_id
        && !f.completed_at
        && f.event?.status !== "completed"
        && f.event?.status !== "cancelled"
      );
      setLiveFixtures(trulyLive.slice(0, 6));
    } catch (err) {
      // Anonymous visitors will 401 on some list endpoints — treat as
      // "zero live fixtures" for the marketing hero. Log in dev so any
      // real failure (500, network) is still visible.
      if (process.env.NODE_ENV !== "production") console.debug("Home.loadLive skipped:", err?.message);
    }
  };

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/teams").then((r) => setTeamMap(Object.fromEntries(r.data.map((t) => [t.id, t])))).catch(() => {});
    loadLive();
  }, []);

  // Real-time: merge in-place if live card already shown; otherwise reload list when a match transitions to/from live.
  useFixtureSocket((payload) => {
    if (!payload || !payload.fixture) return;
    const f = payload.fixture;
    setLiveFixtures((prev) => {
      const idx = prev.findIndex((x) => x.id === f.id);
      if (idx >= 0) {
        if (f.status !== "live") {
          // dropped out of live
          const next = prev.filter((x) => x.id !== f.id);
          return next;
        }
        const next = [...prev];
        next[idx] = { ...next[idx], ...f };
        return next;
      }
      // status went to live - need to fetch event info; trigger reload
      if (f.status === "live") loadLive();
      return prev;
    });
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  });

  return (
    <div className="App bg-[#0a0a0a] min-h-screen text-white">
      <SEO
        title="Kreeda Nation — Book Venues, Run Corporate Sports Events"
        description="India's employee-engagement sports platform. Book turfs, courts and coaches, run corporate tournaments, and manage teams from one dashboard."
        canonical="/"
      />
      <Nav />

      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img
            data-testid="hero-image"
            src={heroImage}
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
          <div className="absolute inset-0 tactical-grid opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white/5 border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] live-pulse" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-300">
              {stats.live > 0 ? `${stats.live} match${stats.live > 1 ? "es" : ""} live now` : "Season in motion"}
            </span>
          </div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mt-6 leading-[0.92] tracking-wide max-w-4xl">
            WHERE TEAMS<br />
            <span className="text-[#EC4899]">COMPETE,</span> <span className="text-[#84CC16]">CONNECT</span><br />
            &amp; <span className="text-[#06B6D4]">GROW.</span>
          </h1>
          <p className="mt-8 max-w-xl text-neutral-300 text-lg leading-relaxed">
            Kreeda Nation turns workplace tournaments into rituals — sports, quizzes, hackathons, all in one
            command center. Register a team, follow live scoring, climb the standings.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register-team">
              <Button data-testid="hero-register-btn" size="lg" className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-semibold rounded-sm px-7 h-12">
                Register Your Team <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/events">
              <Button data-testid="hero-events-btn" size="lg" variant="outline" className="rounded-sm h-12 px-7 border-white/20 bg-white/0 hover:bg-white/5 text-white">
                Browse Events
              </Button>
            </Link>
          </div>

          {/* stat strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mt-20 border border-white/10 rounded-sm overflow-hidden">
            {[
              { label: "Events", value: stats.events, icon: CalendarDays },
              { label: "Teams", value: stats.teams, icon: Users },
              { label: "Players", value: stats.players, icon: Trophy },
              { label: "Live Now", value: stats.live, icon: Activity, accent: true },
            ].map((s) => (
              <div key={s.label} className="bg-[#0a0a0a] p-6">
                <s.icon className={`w-4 h-4 ${s.accent ? "text-[#FF3B30]" : "text-neutral-500"}`} />
                <div className={`font-mono text-4xl mt-3 ${s.accent ? "text-[#FF3B30]" : "text-white"}`}>
                  {String(s.value).padStart(2, "0")}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {false && (
        <SponsorBanner />
      )}

      {/* LIVE ZONE */}
      {liveFixtures.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#FF3B30] uppercase">● Live Zone</div>
              <h2 className="font-display text-5xl mt-2 tracking-wide">HAPPENING NOW</h2>
            </div>
            <Link to="/events" className="text-sm text-neutral-400 hover:text-[#84CC16]">View all →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {liveFixtures.map((f) => (
              <LiveCard key={f.id} fixture={f} teamMap={teamMap} />
            ))}
          </div>
        </section>
      )}

      {/* FOR COMPANIES BAND */}
      <section className="border-y border-white/10 bg-gradient-to-r from-[#84CC16]/10 via-[#06B6D4]/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#84CC16] uppercase">/ For HR & companies</div>
            <h2 className="font-display text-5xl tracking-wide mt-3">RUN YOUR INTERNAL TOURNAMENTS — END-TO-END.</h2>
            <p className="text-neutral-300 mt-4 max-w-xl">
              Onboard your company in 60 seconds. Self-create cricket, football, badminton, hackathon, quiz events.
              Hire <span className="text-[#84CC16]">live YouTube streaming</span>, <span className="text-[#EC4899]">jerseys</span>,
              <span className="text-[#06B6D4]"> trophies</span>, grounds, training kits — all on rental from one dashboard.
            </p>
            <div className="flex gap-3 mt-7">
              <Link to="/signup-company">
                <Button data-testid="home-signup-company" size="lg" className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-semibold rounded-sm px-6 h-12">
                  Onboard your company <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/hire">
                <Button data-testid="home-browse-services" size="lg" variant="outline" className="border-white/20 bg-white/0 text-white rounded-sm px-6 h-12 hover:bg-white/5">
                  Browse marketplace
                </Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { t: "Live Streaming", c: "#EC4899", d: "Multi-cam YouTube broadcast" },
              { t: "Jerseys & Caps", c: "#A855F7", d: "Custom team apparel" },
              { t: "Trophies", c: "#F59E0B", d: "Engraved with team names" },
              { t: "Grounds", c: "#06B6D4", d: "Reserve premium venues" },
              { t: "Match Kits", c: "#84CC16", d: "Bats, balls, scoreboards" },
              { t: "Training", c: "#FF3B30", d: "Coaches & conditioning" },
            ].map((x) => (
              <div key={x.t} className="border border-white/10 rounded-sm p-4 bg-black/40 backdrop-blur">
                <span className="w-2 h-2 rounded-full block" style={{ background: x.c }} />
                <div className="font-display tracking-wider text-xl mt-3">{x.t.toUpperCase()}</div>
                <div className="text-xs text-neutral-400 mt-1">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES Bento */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#84CC16] uppercase">/ Platform</div>
        <h2 className="font-display text-5xl mt-2 tracking-wide">EVERYTHING TO RUN THE SEASON</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          <FeatureCard icon={Users} title="TEAM REGISTRATION" desc="Captains register teams in under 60 seconds. Departments, colors, rosters all in one form." className="md:row-span-2" big />
          <FeatureCard icon={CalendarDays} title="FIXTURE GENERATION" desc="One click — round-robin or knockout bracket, automatically balanced." />
          <FeatureCard icon={Activity} title="LIVE SCORING" desc="Sport-specific scorecards: cricket overs, badminton sets, football goals." accent />
          <FeatureCard icon={Trophy} title="STANDINGS" desc="Real-time leaderboards. Points, win-loss, position swings." />
          <FeatureCard icon={Sparkles} title="PLAYER PROFILES" desc="Stats, jerseys and stories. Stars get the spotlight." />
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-neutral-500 uppercase">/ Calendar</div>
            <h2 className="font-display text-5xl mt-2 tracking-wide">UPCOMING EVENTS</h2>
          </div>
          <Link to="/events" className="text-sm text-neutral-400 hover:text-[#84CC16]" data-testid="home-view-all-events">View all →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {events.map((e) => (
            <Link
              to={`/events/${e.id}`}
              key={e.id}
              data-testid={`home-event-card-${e.id}`}
              className="group relative overflow-hidden rounded-sm border border-white/10 bg-[#141414] hover-lift"
            >
              <div className="h-44 overflow-hidden">
                <img src={e.banner_url || SPORT_IMAGES[e.sport] || SPORT_IMAGES.football} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-80" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border" style={{ borderColor: sportColor(e.sport), color: sportColor(e.sport) }}>
                    {e.sport}
                  </span>
                  <span className="font-mono text-[10px] uppercase text-neutral-500">{e.status}</span>
                </div>
                <h3 className="text-lg font-semibold mt-3 group-hover:text-[#84CC16] transition-colors">{e.name}</h3>
                <p className="text-sm text-neutral-400 mt-1 line-clamp-2">{e.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, accent, big, className = "" }) {
  return (
    <div className={`relative p-8 rounded-sm border border-white/10 bg-[#141414] hover-lift ${className} ${big ? "md:p-10" : ""}`}>
      <Icon className={`w-5 h-5 ${accent ? "text-[#FF3B30]" : "text-[#84CC16]"}`} />
      <div className="font-display tracking-wider text-2xl mt-4">{title}</div>
      <p className="text-sm text-neutral-400 mt-3 max-w-xs leading-relaxed">{desc}</p>
      {big && (
        <div className="absolute right-6 bottom-6 font-mono text-[10px] tracking-widest text-neutral-600">01 / 05</div>
      )}
    </div>
  );
}

function LiveCard({ fixture, teamMap }) {
  const f = fixture;
  const a = teamMap[f.team_a_id];
  const b = teamMap[f.team_b_id];
  return (
    <Link to={`/events/${f.event.id}`} className="block p-5 rounded-sm border border-[#FF3B30]/40 bg-[#141414] hover-lift">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] live-pulse" />
          <span className="font-mono text-[10px] text-[#FF3B30] tracking-widest uppercase">LIVE</span>
        </div>
        <span className="font-mono text-[10px] text-neutral-500 uppercase">{f.event.sport}</span>
      </div>
      <div className="mt-4 space-y-2 font-mono">
        <Row team={a} value={renderScore(f.event.sport, f.score?.team_a)} />
        <Row team={b} value={renderScore(f.event.sport, f.score?.team_b)} />
      </div>
      <div className="text-xs text-neutral-500 mt-4">{f.event.name}</div>
    </Link>
  );
}

function Row({ team, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-1.5">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-5 rounded-sm" style={{ background: team?.color || "#333" }} />
        <span className="text-sm text-neutral-300">{team?.name || "TBD"}</span>
      </div>
      <span className="text-lg text-white">{value}</span>
    </div>
  );
}
